import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, Minus, Plus, ShoppingBag, Trash2, X } from "lucide-react";
import { EASE } from "./anim";
import { useCart } from "@/lib/cart";
import { api, formatApiError } from "@/lib/api";
import { CONTACT } from "@/lib/nav";

export function CartDrawer() {
  const { items, open, setOpen, setQty, remove, clear, subtotal } = useCart();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const reserve = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/orders", {
        ...form,
        items: items.map((i) => ({ product_id: i.id, quantity: i.qty })),
      });
      clear();
      setDone(true);
      setForm({ name: "", email: "", phone: "" });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    setOpen(false);
    setTimeout(() => setDone(false), 400);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-[85] bg-ink/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
          />
          <motion.aside
            data-testid="cart-drawer"
            className="fixed right-0 top-0 z-[86] flex h-full w-full max-w-md flex-col bg-paper shadow-2xl"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.45, ease: EASE }}
          >
            <div className="flex items-center justify-between border-b border-sand px-6 py-5">
              <p className="font-serif text-2xl uppercase text-ink">
                Your <em>bag</em>
              </p>
              <button
                data-testid="cart-close-button"
                onClick={close}
                aria-label="Close cart"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-beige/80 transition-colors hover:bg-blush"
              >
                <X size={16} strokeWidth={1.5} />
              </button>
            </div>

            {done ? (
              <div className="flex flex-1 flex-col items-center justify-center px-8 text-center" data-testid="reserve-success">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-blush">
                  <CheckCircle2 size={24} strokeWidth={1.5} className="text-ink" />
                </span>
                <p className="mt-6 font-serif text-3xl uppercase text-ink">
                  Reserved<em>!</em>
                </p>
                <p className="mt-4 text-sm leading-relaxed text-clay">
                  Your items are set aside. We&rsquo;ll confirm by WhatsApp or email — pay at
                  pickup in store at {CONTACT.address}.
                </p>
                <button onClick={close} data-testid="reserve-continue-button" className="btn btn-dark mt-8">
                  Keep Shopping
                </button>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-6">
                  {items.length === 0 ? (
                    <div className="flex h-full flex-col items-center justify-center text-center" data-testid="cart-empty">
                      <ShoppingBag size={28} strokeWidth={1.2} className="text-sanddeep" />
                      <p className="mt-4 font-serif text-xl text-ink">Your bag is empty</p>
                      <p className="mt-2 text-sm text-clay">Discover brands from our boutique floor.</p>
                      <Link to="/shop" onClick={() => setOpen(false)} className="btn btn-dark mt-6">
                        Shop Here
                      </Link>
                    </div>
                  ) : (
                    <ul className="space-y-5">
                      {items.map((i) => (
                        <li key={i.id} className="flex gap-4" data-testid="cart-item">
                          <img src={i.image} alt={i.name} className="h-24 w-20 rounded-xl object-cover" />
                          <div className="flex flex-1 flex-col justify-between">
                            <div>
                              <p className="text-[10px] uppercase tracking-[0.2em] text-fog">{i.brand}</p>
                              <p className="font-serif text-lg leading-tight text-ink">{i.name}</p>
                              <p className="mt-1 text-sm text-clay">${i.price.toFixed(2)}</p>
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3 rounded-full border border-beige/80 px-3 py-1">
                                <button data-testid="cart-decrease" onClick={() => setQty(i.id, i.qty - 1)} aria-label="Decrease quantity" className="text-clay hover:text-ink">
                                  <Minus size={13} />
                                </button>
                                <span className="text-sm">{i.qty}</span>
                                <button data-testid="cart-increase" onClick={() => setQty(i.id, i.qty + 1)} aria-label="Increase quantity" className="text-clay hover:text-ink">
                                  <Plus size={13} />
                                </button>
                              </div>
                              <button data-testid="cart-remove" onClick={() => remove(i.id)} aria-label="Remove item" className="text-fog transition-colors hover:text-red-700">
                                <Trash2 size={15} strokeWidth={1.5} />
                              </button>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {items.length > 0 && (
                  <form onSubmit={reserve} className="border-t border-sand px-6 py-6" data-testid="reserve-form">
                    <div className="flex justify-between text-sm">
                      <span className="uppercase tracking-[0.2em] text-fog">Subtotal</span>
                      <span className="font-serif text-2xl text-ink" data-testid="cart-subtotal">
                        ${subtotal.toFixed(2)}
                      </span>
                    </div>
                    <div className="mt-4 space-y-3">
                      <input required className="field" placeholder="Your name" value={form.name} onChange={set("name")} data-testid="reserve-name-input" />
                      <input required type="email" className="field" placeholder="Email" value={form.email} onChange={set("email")} data-testid="reserve-email-input" />
                      <input required className="field" placeholder="Phone / WhatsApp" value={form.phone} onChange={set("phone")} data-testid="reserve-phone-input" />
                    </div>
                    {error && <p className="mt-3 text-sm text-red-700" data-testid="reserve-error">{error}</p>}
                    <button
                      type="submit"
                      disabled={busy}
                      data-testid="reserve-button"
                      className="btn btn-dark mt-4 w-full disabled:opacity-60"
                    >
                      {busy ? "Reserving…" : "Reserve Items"}
                    </button>
                    <p className="mt-3 text-center text-[11px] leading-relaxed text-fog">
                      Reserve online, pay at pickup — {CONTACT.address}. We&rsquo;ll confirm by
                      WhatsApp or email.
                    </p>
                  </form>
                )}
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
